import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { fmtDate } from '../../components/RegistrationFormPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// The pay link emailed to a family (/pay/{token}) — no login. Shows the bill and
// opens card checkout (the club's own Square / Stripe account).
export default function PublicPayPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const qs = new URLSearchParams(window.location.search);
  const back = qs.get('done');
  const [checking, setChecking] = useState(!!back);

  useEffect(() => {
    axios.get(`${API_URL}/api/payments/public/${token}`)
      .then(r => setD(r.data)).catch(e => setErr(e?.response?.data?.detail || 'This pay link is not valid.'));
  }, [token]);
  // Back from card checkout: confirm the payment (up to 2 min) before offering Pay again.
  useEffect(() => {
    if (!back) return undefined;
    let n = 0;
    const tick = async () => {
      n += 1;
      try {
        const { data } = await axios.post(`${API_URL}/api/payments/public/${token}/status`, { session_id: qs.get('session_id') || '' });
        setD(x => (x ? { ...x, bill: data.bill } : x));
        if (data.bill.status === 'paid' || data.bill.balance <= 0 || n >= 40) { setChecking(false); return true; }
      } catch { if (n >= 40) { setChecking(false); return true; } }
      return false;
    };
    let t = null;
    const loop = async () => { if (!(await tick())) t = setTimeout(loop, 3000); };
    loop();
    return () => clearTimeout(t);
  }, [token, back]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async (installment) => {
    setBusy(installment ? `i${installment}` : 'all'); setErr('');
    try {
      const { data } = await axios.post(`${API_URL}/api/payments/public/${token}/pay`,
        { origin_url: window.location.origin, installment_number: installment || null });
      const url = data.url || data.checkout_url;
      if (url) { window.location.href = url; return; }
      setErr('Checkout could not start — try again.');
    } catch (e) { setErr(e?.response?.data?.detail || 'Checkout could not start — try again.'); }
    finally { setBusy(''); }
  };

  const b = d?.brand || {};
  const brand = b.primary_color || '#b45309';
  const bill = d?.bill;
  const paid = bill && (bill.status === 'paid' || bill.balance <= 0);
  const open = (bill?.installments || []).filter(i => ['pending', 'partial'].includes(i.status));
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 px-3 py-6" data-testid="public-pay-page">
      <div className="max-w-md mx-auto space-y-3">
        <div className="flex items-center gap-3 rounded-xl px-4 py-3 text-white" style={{ background: brand }}>
          {b.logo_url && <img src={b.logo_url.startsWith('http') ? b.logo_url : `${API_URL}${b.logo_url}`} alt="" className="h-10 w-10 rounded bg-white/90 object-contain p-0.5" />}
          <div className="font-bold">{b.org_name || 'Payment'}</div>
        </div>
        {!d && !err && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>}
        {err && <div className="rounded-xl border bg-white dark:bg-slate-800 p-5 text-sm flex gap-2"><AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />{err}</div>}
        {bill && (
          <div className="rounded-xl border bg-white dark:bg-slate-800 dark:border-slate-700 p-5 space-y-3">
            <div className="text-sm text-slate-500">{bill.for}</div>
            {bill.items.map((i, n) => <div key={n} className="flex justify-between text-sm"><span>{i.description}</span><span>{money(i.amount)}</span></div>)}
            <div className="flex justify-between border-t pt-2 dark:border-slate-700"><span>Total</span><span className="font-semibold">{money(bill.total)}</span></div>
            {bill.paid > 0 && <div className="flex justify-between text-sm text-emerald-700"><span>Paid</span><span>{money(bill.paid)}</span></div>}
            {paid ? (
              <p className="flex items-center gap-2 text-emerald-700 font-medium" data-testid="pay-done"><CheckCircle2 className="h-5 w-5" />Paid in full — thank you!</p>
            ) : (<>
              {checking && <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="pay-checking"><Loader2 className="h-4 w-4 animate-spin" />Confirming your payment…</p>}
              {back && !checking && <p className="text-sm text-slate-500">If you just paid, it can take a minute to show — refresh this page before paying again.</p>}
              <div className="flex justify-between font-bold"><span>Balance</span><span data-testid="pay-balance">{money(bill.balance)}</span></div>
              {checking ? null : open.length > 0 ? open.map(i => (
                <button key={i.number} onClick={() => pay(i.number)} disabled={!!busy} className="w-full h-12 rounded-lg text-white font-semibold flex items-center justify-center gap-2" style={{ background: brand }}>
                  {busy === `i${i.number}` && <Loader2 className="h-4 w-4 animate-spin" />}Pay installment {i.number} — {money(i.left ?? i.amount)}{i.due_date ? ` (due ${fmtDate(i.due_date)})` : ''}
                </button>
              )) : (
                <button onClick={() => pay(null)} disabled={!!busy} className="w-full h-12 rounded-lg text-white font-semibold flex items-center justify-center gap-2" style={{ background: brand }} data-testid="pay-now">
                  {busy === 'all' && <Loader2 className="h-4 w-4 animate-spin" />}Pay {money(bill.balance)}
                </button>
              )}
            </>)}
          </div>
        )}
      </div>
    </div>
  );
}
